/* eslint-disable react-hooks/exhaustive-deps */
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Row, Col, FormGroup, Label, Input } from "reactstrap";
import {
  InvestmentTransactionProvider,
  useInvestmentTransactionContext,
} from "../../contexts/VpsTransaction";
import InvestWithdrawal from "../../components/transactions/InvestWithdrawal";
import AllocationChange from "../../components/transactions/AllocationChange";
import { Button, Modal } from "react-bootstrap";
import { useTheme } from "../../contexts/ThemeContext";
import VpsErrorsModal from "../../utils/vps-erros";
import { useState, useEffect, useMemo } from "react";
import NumberFormat from "react-number-format";
import moment from "moment";

const InvestmentTransactionForm: React.FC = () => {
  return (
    <InvestmentTransactionProvider>
      <ChildComponent />
    </InvestmentTransactionProvider>
  );
};

const ChildComponent = () => {
  const {
    fields,
    updateField,
    amcOptions,
    instrumentTypes,
    loading,
    handleAmcChange,
    handleSubmit,
    getErrorClass,
    handleSaleDateChange,
    history,
    unitholderOptions,
    unitholderLoading,
    handleUnitholderChange,
    handleUnitholderInputChange,
    errors,
    viewMode,
    loadingViewData,
    customStyles,
    id,
    showApproval,
    authroizeDialogue,
    setAuthorizeDialogue,
    decision,
    setDecision,
    rejectReason,
    setRejectReason,
    onSubmitStatus,
    compliant,
    setCompliant,
    compliantComment,
    setcompliantComment,
    navLoading,
    updateNav,
    showVpsErrors,
    setShowVpsErrors,
    vpsErrorMessages,
    // NEW: needed for gross/net calc and parent account
    amount,
    parentFundAccounts,
    handleParentFundAccountChange,
    parentFundAccount,
    coaFunds,
    pageStr,
  } = useInvestmentTransactionContext();

  const [authWithWarnings, setAuthWithWarnings] = useState(false);
  const user = JSON.parse(sessionStorage.getItem("loggedInUser") || "{}");

  const { theme } = useTheme();

  // ─── Derived: gross_amount = total withdrawal amount ───────────────────────
  // Whenever the sub-fund amounts change (reflected in `amount` state), sync
  // gross_amount into fields. Net = gross - all tax/load deductions.
  const isCoaMode =
    viewMode === "changeofallocation" ||
    (viewMode === "view" && fields.txnr_type === "changeofallocation");

  const grossAmount = useMemo(() => {
    if (isCoaMode) {
      return parseFloat(
        coaFunds
          .reduce((sum, f) => sum + (parseFloat(String(f.amount)) || 0), 0)
          .toFixed(4),
      );
    }
    return parseFloat(parseFloat(amount || "0").toFixed(4));
  }, [amount, coaFunds, isCoaMode]);

  const netAmount = useMemo(() => {
    if (isCoaMode) {
      const net = coaFunds.reduce((sum, f) => {
        const amt = parseFloat(String(f.amount)) || 0;
        const backendLoadAmount =
          amt * ((parseFloat(String(f.backend_load)) || 0) / 100);
        const frontendLoadAmount =
          amt * ((parseFloat(String(f.frontend_load)) || 0) / 100);
        return sum + (amt - backendLoadAmount - frontendLoadAmount);
      }, 0);
      return parseFloat(Math.max(0, net).toFixed(4));
    }

    const gross = grossAmount;
    let deductions =
      parseFloat(parseFloat(fields.sale_load).toFixed(2)) +
      parseFloat(fields.other_deductions?.toFixed(2) || "0");

    if (viewMode === "withdrawal")
      deductions += [
        fields.tax_cgt,
        fields.tax_wht_it,
        fields.tax_wht_sst,
        fields.tax_sst,
        fields.tax_other,
      ].reduce((sum, v) => sum + parseFloat(v || "0"), 0);
    return parseFloat(Math.max(0, gross - deductions).toFixed(4));
  }, [
    grossAmount,
    coaFunds,
    isCoaMode,
    fields.tax_cgt,
    fields.tax_wht_it,
    fields.tax_wht_sst,
    fields.tax_sst,
    fields.tax_other,
    fields.sale_load,
    viewMode,
  ]);

  // Keep fields.gross_amount and fields.net_amount in sync
  useEffect(() => {
    if (viewMode !== "view")
      updateField("gross_amount", grossAmount.toFixed(4));
  }, [grossAmount]);

  useEffect(() => {
    if (viewMode !== "view") updateField("net_amount", netAmount.toFixed(4));
  }, [netAmount]);

  // ─── Parent fund collection account options ────────────────────────────────
  // accountOptions is already populated when a fund is selected (investment mode)
  // or when unitholder loads. We expose it as a Select for the parent fund.

  const checkInvestWithdrawal = useMemo(() => {
    return (
      ["withdrawal", "investment"].includes(viewMode) ||
      (viewMode === "view" && fields.txnr_type !== "changeofallocation")
    );
  }, [viewMode, fields.txnr_type]);

  if (viewMode === "view" && loadingViewData)
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <i
          className="fa fa-spinner fa-spin"
          style={{ fontSize: "24px", color: "#007bff" }}
        ></i>
        <div>Loading Transaction data...</div>
      </div>
    );

  // Tax field helper — renders an editable NumberFormat in withdrawal mode,
  // read-only input in view mode.
  const TaxField = ({
    label,
    fieldKey,
  }: {
    label: string;
    fieldKey: keyof typeof fields;
  }) => {
    const isEditable = viewMode === "withdrawal";
    return (
      <div className="col-md-6">
        <div className="form-group">
          <label>{label}</label>
          {isEditable ? (
            <NumberFormat
              thousandSeparator={true}
              decimalScale={4}
              allowNegative={false}
              className="form-control"
              value={fields[fieldKey] as string}
              onValueChange={({ value }) =>
                updateField(fieldKey as any, value || "0")
              }
              placeholder="0"
            />
          ) : (
            <input
              type="text"
              className="form-control"
              value={(fields[fieldKey] as string) || "0"}
              readOnly
              disabled
            />
          )}
        </div>
      </div>
    );
  };

  const rejectedEditCheck = viewMode === "view" || pageStr === "edit";

  return (
    <>
      <div className="content">
        <ToastContainer />

        {/* Header */}
        <div className="p-xl-3">
          <div className="input-holder mb-3">
            <h4 className="card-title">
              <i
                className="fa fa-angle-left mr-2 pointer"
                onClick={() => {
                  const route = id?.split(":")[1];
                  if (viewMode === "view" || route === "rejected-transaction")
                    history.push(`/admin/${id?.split(":")[1]}`);
                  else history.push("/admin/post-transaction");
                }}
              ></i>
              {viewMode.toUpperCase()} Transaction
            </h4>
            {viewMode !== "view" && (
              <button
                className="btn btn-info btn-sm mt-a ml-4"
                onClick={() => {
                  history.replace(`/admin/post-transaction-uploader`);
                }}
              >
                <i className="fa fa-upload mr-2"></i>Upload
              </button>
            )}
            {viewMode === "view" && vpsErrorMessages?.length > 0 && (
              <button
                className="btn btn-sm mt-a ml-4"
                style={{
                  background: "#f7ab06",
                }}
                onClick={() => {
                  setShowVpsErrors(true);
                }}
              >
                View Errors
              </button>
            )}
          </div>
          <Row>
            {/* Instruction Details Card */}
            <Col>
              <div className="card mb-3">
                <div className="card-header text-white">
                  <h5 className="mb-0">
                    <i className="fa fa-file-text mr-2"></i>
                    Instruction Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {user.user_type === "TRUSTEE" && (
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>
                            AMC <span className="text-danger">*</span>
                          </label>
                          <Select
                            options={amcOptions}
                            isDisabled={viewMode === "view"}
                            onChange={handleAmcChange}
                            placeholder="Select AMC"
                            className={getErrorClass(fields.amc_code)}
                            value={
                              amcOptions.find(
                                (opt) => opt.value === fields.amc_code,
                              ) || null
                            }
                            styles={customStyles}
                          />
                        </div>
                      </div>
                    )}
                    <div className="col-md-12">
                      <div className="form-group">
                        <label>Instruction Date</label>
                        <input
                          type="date"
                          className="form-control"
                          disabled={viewMode === "view"}
                          value={fields.instruction_date}
                          onChange={(e) =>
                            updateField("instruction_date", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-group">
                        <label>
                          {fields.txnr_type === "withdrawal"
                            ? "Payment"
                            : "Realization"}{" "}
                          Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          disabled={viewMode === "view"}
                          value={fields.settlement_date}
                          onChange={(e) =>
                            updateField("settlement_date", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            {/* Unitholder Account Details Card */}
            <Col>
              <div className="card mb-3">
                <div className="card-header text-white">
                  <h5 className="mb-0">
                    <i className="fa fa-user mr-2"></i>
                    Unitholder Account Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label>
                          Search & Select Unitholder{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={unitholderOptions}
                          onChange={handleUnitholderChange}
                          onInputChange={handleUnitholderInputChange}
                          placeholder="Search by folio number or name..."
                          isDisabled={!fields.amc_code || viewMode === "view"}
                          isLoading={unitholderLoading}
                          isClearable
                          className={getErrorClass(fields.folio_no)}
                          value={
                            unitholderOptions.find(
                              (opt) => opt.folio_no === fields.folio_no,
                            ) || null
                          }
                          noOptionsMessage={() =>
                            unitholderLoading
                              ? "Searching..."
                              : "No unitholders found. Try a different search."
                          }
                          filterOption={null}
                          styles={customStyles}
                        />
                        <small className="text-muted">
                          Type folio number or name to search
                        </small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Folio Number</label>
                        <input
                          type="text"
                          className="form-control"
                          value={fields.folio_no}
                          readOnly
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Unit Holder Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={fields.unitholder_name}
                          readOnly
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label>
                          {fields.txnr_type === "investment"
                            ? "Sender"
                            : "Beneficiary"}{" "}
                          Account Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={fields.counter_account_number || "—"}
                          readOnly
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>
                          {fields.txnr_type === "investment"
                            ? "Sender"
                            : "Beneficiary"}{" "}
                          Account Title
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={fields.counter_account_title || "—"}
                          readOnly
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label>
                          {fields.txnr_type === "investment"
                            ? "Sender"
                            : "Beneficiary"}{" "}
                          Bank
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={fields.counter_bank}
                          disabled
                          placeholder="Enter counter bank"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label>
                          {fields.txnr_type === "investment"
                            ? "Sender"
                            : "Beneficiary"}{" "}
                          Branch
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={fields.counter_branch}
                          disabled
                          placeholder="Enter counter branch"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col className="col-md-4">
              {/* Instrument Details Card */}
              <div className="card mb-3">
                <div className="card-header text-white">
                  <h5 className="mb-0">
                    <i className="fa fa-credit-card mr-2"></i>
                    Instrument Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label>
                          Instrument Type
                          <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-control ${errors["instrument_type"] ? "text-danger border-danger" : ""}`}
                          disabled={
                            viewMode === "view" &&
                            !(fields.txnr_type === "withdrawal" && showApproval)
                          }
                          value={fields.instrument_type}
                          onChange={(e) =>
                            updateField("instrument_type", e.target.value)
                          }
                        >
                          <option value="">Select Type</option>
                          {instrumentTypes.map((t, i) => (
                            <option key={i} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      {errors["instrument_type"] && (
                        <span className="text-danger">
                          {errors["instrument_type"]}
                        </span>
                      )}
                    </div>

                    <div className="col-md-12">
                      <div className="form-group">
                        <label>
                          Instrument Number
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${errors["instrument_no"] ? "text-danger border-danger" : ""}`}
                          disabled={
                            viewMode === "view" &&
                            !(fields.txnr_type === "withdrawal" && showApproval)
                          }
                          value={fields.instrument_no}
                          onChange={(e) =>
                            updateField("instrument_no", e.target.value)
                          }
                          placeholder="Enter instrument number"
                        />
                      </div>
                      {errors["instrument_no"] && (
                        <span className="text-danger">
                          {errors["instrument_no"]}
                        </span>
                      )}
                    </div>

                    <div className="col-md-12">
                      <Row>
                        <Col className="form-group">
                          <label>Nav Date</label>
                          <input
                            type="date"
                            disabled={viewMode === "view"}
                            className="form-control"
                            value={fields.sale_date}
                            onChange={(e) => {
                              updateField("sale_date", e.target.value);
                            }}
                          />
                        </Col>
                        {viewMode !== "view" && (
                          <Col>
                            <button
                              disabled={navLoading}
                              onClick={() => updateNav()}
                              className={`btn btn-secondary mt-4`}
                            >
                              {navLoading && (
                                <i className="tim-icons tim-icons-spin"></i>
                              )}
                              {navLoading ? "...loading" : "load"}
                            </button>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="mb-3 d-flex align-items-stretch">
            {/* 1. Transaction Summary */}
            <Col md="4" className="mb-3">
              <div className="card h-100">
                <div className="card-header text-white">
                  <h5 className="mb-0">
                    <i
                      className={`fa mr-2 ${
                        fields.txnr_type === "investment"
                          ? "fa-arrow-circle-down"
                          : "fa-arrow-circle-up"
                      }`}
                    />
                    Transaction Summary
                  </h5>
                </div>
                <div className="card-body">
                  <div className="form-group">
                    <label>Gross Amount</label>
                    <NumberFormat
                      thousandSeparator={true}
                      decimalScale={4}
                      className="form-control font-weight-bold"
                      value={
                        viewMode === "view" ? fields.gross_amount : grossAmount
                      }
                      disabled
                    />
                  </div>
                  <div className="form-group">
                    <label>Net Amount</label>
                    <NumberFormat
                      thousandSeparator={true}
                      decimalScale={4}
                      className={`form-control font-weight-bold ${
                        netAmount < grossAmount
                          ? "text-warning"
                          : "text-success"
                      }`}
                      value={
                        viewMode === "view" ? fields.net_amount : netAmount
                      }
                      disabled
                    />
                  </div>

                  <div className="form-group">
                    <label>Creation Details</label>
                    <input
                      type="text"
                      className="form-control"
                      value={fields.detail}
                      onChange={(e) => {
                        updateField("detail", e.target.value);
                      }}
                      disabled={viewMode === "view"}
                    />
                  </div>
                  {checkInvestWithdrawal && (
                    <>
                      <div className="form-group">
                        <label>
                          {fields.txnr_type === "withdrawal" ||
                          viewMode === "withdrawal"
                            ? "Redemption"
                            : "Sale"}{" "}
                          Load
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={fields.sale_load || "0"}
                          onChange={(e) => {
                            updateField("sale_load", e.target.value);
                          }}
                          disabled={viewMode === "view"}
                        />
                      </div>

                      <div className="form-group">
                        <label>Other Deductions</label>
                        <NumberFormat
                          thousandSeparator={true}
                          decimalScale={4}
                          allowNegative={false}
                          className="form-control"
                          value={fields.other_deductions ?? 0}
                          onValueChange={({ value }) =>
                            updateField(
                              "other_deductions",
                              value ? parseFloat(value) : 0,
                            )
                          }
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Col>

            {/* 2. Tax & Deductions Panel */}
            {(viewMode === "withdrawal" ||
              (viewMode === "view" && fields.txnr_type === "withdrawal")) && (
              <Col md="4" className="mb-3">
                <div className="card h-100">
                  <div className="card-header text-white">
                    <h5 className="mb-0">
                      <i className="fa fa-percent mr-2" />
                      Tax &amp; Deductions
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <TaxField label="CGT" fieldKey="tax_cgt" />
                      <TaxField label="WHT (IT)" fieldKey="tax_wht_it" />
                      <TaxField label="SST" fieldKey="tax_sst" />
                    </div>

                    {/* WHT Details Section */}
                    <hr />
                    <h6 className="text-muted mb-3">
                      <i className="fa fa-info-circle mr-1" />
                      WHT Details
                    </h6>
                    <div className="row">
                      {/* WHT Payment Code */}
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>WHT Payment Code</label>
                          {viewMode === "withdrawal" ? (
                            <input
                              type="text"
                              className="form-control"
                              value={fields.wht_payment_code || ""}
                              onChange={(e) =>
                                updateField("wht_payment_code", e.target.value)
                              }
                              placeholder="Enter WHT payment code"
                            />
                          ) : (
                            <input
                              type="text"
                              className="form-control"
                              value={fields.wht_payment_code || "—"}
                              readOnly
                              disabled
                            />
                          )}
                        </div>
                      </div>

                      {/* WHT Taxable Amount */}
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>WHT Taxable Amount</label>
                          {viewMode === "withdrawal" ? (
                            <NumberFormat
                              thousandSeparator={true}
                              decimalScale={4}
                              allowNegative={false}
                              className="form-control"
                              value={fields.wht_taxable_amount ?? 0}
                              onValueChange={({ value }) =>
                                updateField(
                                  "wht_taxable_amount",
                                  value ? parseFloat(value) : 0,
                                )
                              }
                              placeholder="0"
                            />
                          ) : (
                            <input
                              type="text"
                              className="form-control"
                              value={fields.wht_taxable_amount ?? "0"}
                              readOnly
                              disabled
                            />
                          )}
                        </div>
                      </div>

                      {/* Year columns header */}
                      <div className="col-md-12 mb-1">
                        <div className="row text-center">
                          <div className="col-md-4">
                            <small className="text-muted font-weight-bold">
                              Year 1
                            </small>
                          </div>
                          <div className="col-md-4">
                            <small className="text-muted font-weight-bold">
                              Year 2
                            </small>
                          </div>
                          <div className="col-md-4">
                            <small className="text-muted font-weight-bold">
                              Year 3
                            </small>
                          </div>
                        </div>
                      </div>

                      {/* WHT Tax Paid — Years 1, 2, 3 */}
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>WHT Tax Paid</label>
                          <div className="row">
                            {(
                              [
                                ["wht_tax_paid_year_1", "Year 1"],
                                ["wht_tax_paid_year_2", "Year 2"],
                                ["wht_tax_paid_year_3", "Year 3"],
                              ] as const
                            ).map(([key, placeholder]) => (
                              <div className="col-md-4 pr-1 pl-1" key={key}>
                                {viewMode === "withdrawal" ? (
                                  <NumberFormat
                                    thousandSeparator={true}
                                    decimalScale={4}
                                    allowNegative={false}
                                    className="form-control"
                                    value={fields[key] ?? 0}
                                    onValueChange={({ value }) =>
                                      updateField(
                                        key,
                                        value ? parseFloat(value) : 0,
                                      )
                                    }
                                    placeholder={placeholder}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={fields[key] ?? "0"}
                                    readOnly
                                    disabled
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* WHT Taxable Income — Years 1, 2, 3 */}
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>WHT Taxable Income</label>
                          <div className="row">
                            {(
                              [
                                ["wht_taxable_income_year_1", "Year 1"],
                                ["wht_taxable_income_year_2", "Year 2"],
                                ["wht_taxable_income_year_3", "Year 3"],
                              ] as const
                            ).map(([key, placeholder]) => (
                              <div className="col-md-4 pr-1 pl-1" key={key}>
                                {viewMode === "withdrawal" ? (
                                  <NumberFormat
                                    thousandSeparator={true}
                                    decimalScale={4}
                                    allowNegative={false}
                                    className="form-control"
                                    value={fields[key] ?? ""}
                                    onValueChange={({ value }) =>
                                      updateField(
                                        key,
                                        value ? parseFloat(value) : 0,
                                      )
                                    }
                                    placeholder={placeholder}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={fields[key] ?? "0"}
                                    readOnly
                                    disabled
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            )}

            {/* 3. Collection Account Panel */}
            {checkInvestWithdrawal && (
              <Col md="4" className="mb-3">
                <div className="card h-100">
                  <div className="card-header text-white">
                    <h5 className="mb-0">
                      <i className="fa fa-university mr-2"></i>
                      Collection Account
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="form-group">
                      <label>
                        Collection Account{" "}
                        <span className="text-danger">*</span>
                      </label>
                      {rejectedEditCheck ? (
                        <input
                          className="form-control"
                          disabled
                          value={
                            `${fields.account_title}-${fields.account_number}` ||
                            ""
                          }
                        />
                      ) : (
                        <Select
                          options={parentFundAccounts}
                          value={parentFundAccount}
                          onChange={handleParentFundAccountChange}
                          isDisabled={
                            !["investment", "withdrawal"].includes(viewMode)
                          }
                          styles={customStyles}
                        />
                      )}
                    </div>
                    <div className="form-group">
                      <label>Account Number</label>
                      <input
                        type="text"
                        className="form-control "
                        value={fields.account_number || "—"}
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <label>Account Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={fields.account_title || "—"}
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <label>Bank / Branch</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          fields.bank
                            ? `${fields.bank} (${fields.branch || ""})`
                            : "—"
                        }
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </Col>
            )}
          </Row>
          <InvestWithdrawal />
          <AllocationChange />
          {viewMode !== "view" && (
            <Row>
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-12 text-right">
                      <button
                        className="btn btn-secondary mr-2"
                        onClick={() => history.push("/admin/post-transaction")}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={
                          !!loading ||
                          (viewMode === "withdrawal" && !!errors.withdrawal)
                        }
                      >
                        {loading ? (
                          <>
                            <i className="fa fa-spinner fa-spin mr-2"></i>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-check mr-2"></i>
                            Submit{" "}
                            {viewMode === "withdrawal"
                              ? "Withdrawal"
                              : viewMode === "changeofallocation"
                                ? "Change of Allocation"
                                : "Investment"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Row>
          )}
          {viewMode === "view" &&
            fields.txn_status !== "COMPLETED" &&
            showApproval && (
              <Row>
                <Button
                  className="btn btn-primary"
                  onClick={() => setAuthorizeDialogue(true)}
                >
                  Action
                </Button>
              </Row>
            )}
          <div className="line"></div>
          <Row>
            {fields?.txn_history.map((hist: any, index: number) => (
              <Col
                md="3"
                onClick={() => {
                  if (hist.action === "CREATED") return;
                  if (!hist.check_list) return;
                }}
              >
                <p className="number-text"></p>
                <div className="card process-card pointer">
                  <div className="card-header">
                    <h4 className="card-title">
                      <span className="card-number">{index + 1}</span>
                      <i className="tim-icons icon-single-02 mx-1"></i>
                      {hist.updated_by_role}
                    </h4>
                  </div>

                  <div className="card-body text-center">
                    <p className="small mt-2">Status:</p>
                    <h3 className="accepted text-primary">{hist.action}</h3>

                    {hist.updated_at !== "" && (
                      <>
                        <p className="small mt-2">by</p>
                        <h4 className="comment text-primary">
                          {hist.updated_by}
                        </h4>
                        <p className="small mt-2">at</p>
                        <h4 className="comment text-primary">
                          {moment(hist.updated_at).format("DD-MM-YYYY, h:mm a")}
                        </h4>
                      </>
                    )}

                    {hist.created_by !== "" && (
                      <>
                        <p className="small mt-2">Created By</p>
                        <h4>{hist.created_by}</h4>
                        <p className="small mt-2">Created At</p>
                        <h4 className="comment">
                          {moment(hist.created_at).format("DD-MM-YYYY")}
                        </h4>
                      </>
                    )}
                  </div>
                </div>
              </Col>
            ))}
          </Row>{" "}
        </div>
      </div>

      {/* ── Approval Modal ──────────────────────────────────────────────────────── */}
      {authroizeDialogue && (
        <Modal
          className=""
          dialogClassName="modal90w"
          show={true}
          enforceFocus={false}
          size="xl"
        >
          <div className="modal-header">
            <button
              aria-hidden={true}
              className="close"
              data-dismiss="modal"
              type="button"
              onClick={() => setAuthorizeDialogue(false)}
            >
              <i className="tim-icons icon-simple-remove" />
            </button>
            <h6 className="title title-up">Approval Check List</h6>
          </div>
          <div className="modal-body">
            <div className="">
              <Row className="pt-3">
                <Col md="5">
                  <text className="text-primary">
                    {fields.txn_id} : {fields.txnr_type}{" "}
                  </text>{" "}
                  <text>
                    {fields.folio_no}-{fields.unitholder_name}
                  </text>
                </Col>
                <Col md="7">
                  <Row>
                    <Col md="3">
                      <FormGroup check>
                        <Label check>
                          <Input
                            checked={compliant === "compliant"}
                            type="checkbox"
                            value="compliant"
                            onChange={(e) => setCompliant(e.target.value || "")}
                          />
                          <span className="form-check-sign" />
                          Compliant
                        </Label>
                      </FormGroup>
                    </Col>
                    <Col md="2">
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            checked={compliant === "na"}
                            value="na"
                            onChange={(e: any) =>
                              setCompliant(e.target.value || "")
                            }
                          />
                          <span className="form-check-sign" />
                          N/A
                        </Label>
                      </FormGroup>
                    </Col>
                    <Col md="7">
                      <FormGroup>
                        <input
                          type="text"
                          placeholder="Comments"
                          value={compliantComment}
                          onChange={(e) =>
                            setcompliantComment(e.target.value || "")
                          }
                          className="form-control"
                          disabled={compliant === "na"}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="mt-4">
                <Col md="12">
                  <FormGroup>
                    <label>Decision</label>
                    <div className="d-flex">
                      <FormGroup check className="mr-4">
                        <Label check>
                          <Input
                            type="checkbox"
                            name="decision"
                            value="accept"
                            checked={decision === "accept"}
                            onChange={(e) => {
                              setDecision(e.target.value);
                              setRejectReason("");
                            }}
                          />
                          <span className="form-check-sign" />
                          Accept
                        </Label>
                      </FormGroup>
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            name="decision"
                            value="reject"
                            checked={decision === "reject"}
                            onChange={(e) => setDecision(e.target.value)}
                          />
                          <span className="form-check-sign" />
                          Reject
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                </Col>
              </Row>

              {decision === "reject" && (
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <label>Reason for Rejection *</label>
                      <textarea
                        placeholder="Enter reason for rejection"
                        className="form-control"
                        rows={4}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                      ></textarea>
                    </FormGroup>
                  </Col>
                </Row>
              )}

              <Row>
                <Col>
                  <div>
                    <button
                      className="btn btn-primary ml-2"
                      onClick={() => {
                        if (!decision) {
                          toast.error("Please select Accept or Reject");
                          return;
                        }
                        if (decision === "reject" && !rejectReason.trim()) {
                          toast.error("Please provide a reason for rejection");
                          return;
                        }

                        const submitData: any = {
                          txn_id: fields._id,
                          status: decision,
                          ...(fields.txnr_type === "withdrawal" && {
                            instrument_type: fields.instrument_type,
                            instrument_no: fields.instrument_no,
                          }),
                          ...(fields.txnr_type === "changeofallocation" && {
                            subFunds: coaFunds,
                          }),
                        };

                        if (decision === "reject")
                          submitData.reasons = rejectReason;
                        if (compliant === "compliant") {
                          if (!compliantComment)
                            toast.error("Please provide compliant comment");
                          submitData.compliantComment = compliantComment;
                        }
                        if (authWithWarnings)
                          submitData.auth_with_errors = authWithWarnings;

                        onSubmitStatus(submitData);
                        setAuthorizeDialogue(false);
                        setDecision("");
                        setRejectReason("");
                        setCompliant("");
                        setcompliantComment("");
                      }}
                    >
                      Submit
                    </button>
                    <button
                      className="btn btn-default"
                      onClick={() => {
                        setAuthorizeDialogue(false);
                        setDecision("");
                        setRejectReason("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Modal>
      )}

      <VpsErrorsModal
        showVpsErrors={showVpsErrors}
        setShowVpsErrors={setShowVpsErrors}
        vpsErrorMessages={vpsErrorMessages}
        isViewTxn={true}
        setAuthWithWarnings={setAuthWithWarnings}
        setAuthModal={() => setAuthorizeDialogue(true)}
      />
    </>
  );
};

export default InvestmentTransactionForm;
